<template>
  <div
      class="order-return-edit-customer-details flex flex-auto flex-col">
    <div class="title">
      Review customer details and address
    </div>
    <div class="description">
      Review and edit the customer details to make sure all info is correct.
    </div>
    <el-alert
        v-if="!customerUIState.authorized && customerUIState.valid"
        :closable="false"
        :title="findAlertByKey('customerAuthorizationRequired').title"
        type="warning"
        :description="findAlertByKey('customerAuthorizationRequired').description"
        show-icon />
    <el-alert
        v-if="!customerUIState.valid"
        :closable="false"
        :title="findAlertByKey('customerEmailNotFound').title"
        type="warning"
        :description="findAlertByKey('customerEmailNotFound').description"
        show-icon />
    <div style="margin-top: 24px">
      <div class="section-header">
        Customer details
      </div>
    </div>
    <div class="edit-customer-details">
      <div class="flex flex-col gap_s">
        <div class="flex flex-row gap-16">
          <div class="flex-1 input-label">
            First Name
          </div>
          <div class="flex-1 input-label">
            Last Name
          </div>
        </div>
        <div class="flex flex-row gap-16">
          <input
              v-model="editedCustomer.first_name"
              class="flex-1 input-field"
              style="height: 48px;">
          <input
              v-model="editedCustomer.last_name"
              class="flex-1 input-field"
              style="height: 48px;">
        </div>
        <div
            v-if="errors.first_name || errors.last_name"
            class="flex flex-row gap-16">
          <div
              class="flex-1 input-error">
            {{ errors.first_name }}
          </div>
          <div
              class="flex-1 input-error">
            {{ errors.last_name }}
          </div>
        </div>
        <div class="flex flex-row gap-16">
          <div class="flex-1 input-label">
            Email
          </div>
          <div class="flex-1 input-label">
            Phone Number
          </div>
        </div>
        <div class="flex flex-row gap-16">
          <input
              v-model="editedCustomer.email"
              class="flex-1 input-field"
              style="height: 48px;"
              @blur="onBlur"
              @mouseleave="onMouseLeave">
          <vue-tel-input
              :key="country_code"
              v-model="editedCustomer.phone_number"
              :default-country="country_code"
              mode="international"
              class="flex-1 input-field"
              style="height: 48px;"
              @country-changed="updatePhone"
              @validate="updatePhoneValidation">
            <i
                slot="arrow-icon"
                class="el-dialog__close el-icon el-icon-arrow-down" />
          </vue-tel-input>
        </div>
        <div
            v-if="errors.email || errors.phone_number"
            class="flex flex-row gap-16">
          <div
              class="flex-1 input-error">
            {{ errors.email }}
          </div>
          <div
              class="flex-1 input-error">
            {{ errors.phone_number }}
          </div>
        </div>
      </div>
    </div>
    <div style="margin-top: 16px">
      <div class="section-header">
        Address
      </div>
    </div>
    <div class="edit-customer-details">
      <div class="flex flex-col gap_s">
        <div class="flex flex-row gap-16">
          <div class="flex-1 input-label">
            Address Line 1
          </div>
        </div>
        <div
            class="flex flex-row gap-16">
          <el-input
              v-model="editedAddress.address1"
              v-google-places="googleOn"
              class="flex-1 input-field"
              style="padding: 0px;height: 48px;"
              @selected="placeSelected" />
        </div>
        <div class="flex flex-row gap-16">
          <div
              v-if="errors.address1"
              class="flex-1 input-error">
            {{ errors.address1 }}
          </div>
        </div>
        <div class="flex flex-row gap-16">
          <div class="flex-1 input-label">
            Address Line 2
          </div>
        </div>
        <div class="flex flex-row gap-16">
          <el-input
              v-model="editedAddress.address2"
              class="flex-1 input-field"
              style="padding: 0px;height: 48px;" />
        </div>
        <div class="flex flex-row gap-16">
          <div
              v-if="errors.address2"
              class="flex-1 input-error">
            {{ errors.address2 }}
          </div>
        </div>
        <div class="flex flex-row gap-16">
          <div class="flex-1 input-label">
            Country
          </div>
          <div class="flex-1 input-label">
            State
          </div>
        </div>
        <div class="flex flex-row gap-16">
          <country-input
              v-model="country_code"
              class="flex-1 select-field"
              :value="country_code" />
          <country-state-input
              v-model="state_code"
              :input-type="'select'"
              class="flex-1 select-field"
              :country="country_code" />
        </div>
        <div
            v-if="errors.country || errors.state"
            class="flex flex-row gap-16">
          <div
              class="flex-1 input-error">
            {{ errors.country }}
          </div>
          <div
              class="flex-1 input-error">
            {{ errors.state }}
          </div>
        </div>
        <div class="flex flex-row gap-16">
          <div class="flex-1 input-label">
            City
          </div>
          <div class="flex-1 input-label">
            Zip code
          </div>
        </div>
        <div class="flex flex-row gap-16">
          <el-input
              v-model="editedAddress.city"
              class="flex-1 input-field"
              style="padding: 0px;height: 48px" />
          <el-input
              v-model="editedAddress.zip_code"
              class="flex-1 input-field"
              style="padding: 0px;height: 48px;" />
        </div>
        <div
            v-if="errors.city || errors.zip_code"
            class="flex flex-row gap-16">
          <div
              class="flex-1 input-error">
            {{ errors.city }}
          </div>
          <div
              class="flex-1 input-error">
            {{ errors.zip_code }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { get, head } from "lodash";
import CountryStateInput from "@/components/Shared/AddressForm/CountryStateInput.vue";
import CountryInput from "@/components/Shared/AddressForm/CountryInput.vue";
import { createNamespacedHelpers as module } from "vuex";
import { isObfuscatedData } from "@/utils/utils";

const { mapGetters: sharedGetters, mapActions: sharedActions } =
  module("shared");
const { mapActions: accountActions, mapGetters: accountGetters } =
  module("db/account");

export default {
  name: "EditCustomerDetail",
  components: { CountryInput, CountryStateInput },
  props: {
    customer: {
      type: Object,
      required: true,
      default() {
        return {
          first_name: "",
          last_name: "",
          email: "",
          phone_number: "",
        };
      },
    },
    address: {
      type: Object,
      required: true,
      default() {
        return {
          address1: "",
          address2: "",
          city: "",
          country: "",
          state: "",
          zip_code: "",
        };
      },
    },
  },
  data() {
    return {
      editedCustomer: {
        first_name: "",
        last_name: "",
        email: "",
        phone_number: "",
      },
      previousEmail: "",
      validPhone: true,
      editedAddress: {
        address1: "",
        address2: "",
        city: "",
        country: "",
        state: "",
        zip_code: "",
      },
      country_code: "",
      state_code: "",
      googleOn: true,
      customerUIState: {
        authorized: true,
        valid: true,
        validating: false,
      },
      messages: [
        {
          key: "customerAuthorizationRequired",
          title: "Customer Authorization Required",
          description:
            "This account is not authorized to show PII information. Please authorize it on customer's page, you will not be able to continue with this request until then.",
        },
        {
          key: "customerEmailNotFound",
          title: "Email not found",
          description:
            "Email can be set only to addresses which are registered to a Ring login account (i.e. the account used to login to the mobile app).",
        },
      ],
    };
  },
  computed: {
    ...sharedGetters(["countries", "countryStates"]),
    ...accountGetters(["locations"]),
    errors() {
      return {
        first_name: this.validateFirstName,
        last_name: this.validateLastName,
        email: this.validateEmail,
        phone_number: this.validatePhone,
        address1: this.validateAddressLine1,
        address2: this.validateAddressLine2,
        country: this.validateCountry,
        state: this.validateState,
        city: this.validateCity,
        zip_code: this.validateZip,
      };
    },
    allValid() {
      return !(
        this.validateFirstName ||
        this.validateLastName ||
        this.validateEmail ||
        this.validatePhone ||
        this.validateAddressLine1 ||
        this.validateAddressLine2 ||
        this.validateCountry ||
        this.validateState ||
        this.validateCity ||
        this.validateZip ||
        !this.customerUIState.valid ||
        !this.customerUIState.authorized ||
        this.customerUIState.validating
      );
    },
    validateFirstName() {
      if (
        this.editedCustomer.first_name === "" ||
        this.editedCustomer.first_name === null
      )
        return "First Name is Required";
      return "";
    },
    validateLastName() {
      if (
        this.editedCustomer.last_name === "" ||
        this.editedCustomer.last_name === null
      )
        return "Last Name is Required";
      return "";
    },
    validateEmail() {
      if (
        this.editedCustomer.email === "" ||
        this.editedCustomer.email === null
      )
        return "Email is Required";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.editedCustomer.email)) {
        return "Invalid Email";
      }
      return "";
    },
    validatePhone() {
      if (
        this.editedCustomer.phone_number === "" ||
        this.editedCustomer.phone_number === null
      )
        return "Phone is Required";
      if (!this.validPhone) return "Invalid Phone Number";
      return "";
    },
    validateAddressLine1() {
      if (
        this.editedAddress.address1 === "" ||
        this.editedAddress.address1 === null
      )
        return "Address Line 1 is Required";
      return "";
    },
    validateAddressLine2() {
      //Disabling validation for address line 2 as in many addresses this field might not be present
      return "";
    },
    validateCountry() {
      if (this.country_code == null || this.country_code === "")
        return "Please select a country";
      return "";
    },
    validateState() {
      if (this.state_code == null || this.state_code === "")
        return "Please select a State";
      return "";
    },
    validateCity() {
      if (this.editedAddress.city === "" || this.editedAddress.city === null)
        return "City is Required";
      return "";
    },
    validateZip() {
      if (
        this.editedAddress.zip_code === "" ||
        this.editedAddress.zip_code === null
      )
        return "Zip is Required";
      return "";
    },
  },
  watch: {
    allValid: {
      handler(value) {
        this.$emit("update:allvalid", value);
      },
      immediate: true,
    },
  },
  mounted() {
    // Set incoming address into edited address field so that validations can be applied on edited address
    Object.assign(this.editedAddress, this.address);
    this.fetchCountries().finally(() => {
      //If we dont get country code, we will store the incoming country name. Country code is needed to get list of states
      this.country_code =
        this.findCountry(this.editedAddress.country) ||
        this.editedAddress.country;
      this.fetchCountryStates(String(this.country_code).toUpperCase()).finally(
        () => {
          this.state_code = this.findState(this.editedAddress.state);
        }
      );
    });

    Object.assign(this.editedCustomer, this.customer);
  },
  methods: {
    isObfuscatedData,
    ...sharedActions(["fetchCountries", "fetchCountryStates"]),
    ...accountActions(["globalSearch"]),
    findAlertByKey(alertKey) {
      return this.messages.find((alert) => alert.key === alertKey);
    },
    onBlur() {
      this.isTyping = false;
      this.searchCustomerIfChanged();
    },
    onMouseLeave() {
      if (!this.isTyping) {
        this.searchCustomerIfChanged();
      }
    },
    saveChanges() {
      this.$emit("update:customer", this.editedCustomer);
      this.editedAddress.country = this.countries[this.country_code].sname;
      let stateList = this.countryStates(this.country_code)[this.state_code];
      this.editedAddress.state = stateList ? stateList.code : this.state_code;
      this.$emit("update:address", this.editedAddress);
    },
    findCountry(countryName) {
      for (const countryCode in this.countries) {
        if (this.countries[countryCode].name === countryName)
          return countryCode;
      }
      return null;
    },
    findState(stateName) {
      let countryStates = this.countryStates(this.country_code);
      for (const stateCode in countryStates) {
        if (countryStates[stateCode].name === stateName) return stateCode;
      }
      return stateName;
    },
    findStateCode(gPlace) {
      let countryStates = this.countryStates(this.country_code);
      for (const stateCode in countryStates) {
        if (stateCode === gPlace.stateCode) return stateCode;
      }
      return gPlace.state; //When state does not exist in countryStates list, at that time both state code and state name will have same value state name
    },
    updatePhoneValidation(phoneObject) {
      this.validPhone = !!phoneObject.valid;
    },
    updatePhone(phoneObject) {
      phoneObject.countryCode = this.country_code;
    },
    placeSelected(gPlace) {
      this.editedAddress.address1 = gPlace.address1;
      this.editedAddress.address2 = gPlace.address2;
      this.editedAddress.city = gPlace.city;
      this.editedAddress.zip_code = gPlace.zip;
      this.editedAddress.state = gPlace.state;
      this.country_code = gPlace.countryCode;
      this.state_code = this.findStateCode(gPlace);
    },
    fetchCustomerData() {
      const storedCustomerData = this.$store.getters["db/account/customer"];
      if (head(storedCustomerData.locations).address) {
        this.editedAddress = head(storedCustomerData.locations).address;
        this.country_code =
          this.findCountry(this.editedAddress.country) ||
          this.editedAddress.country;
        this.fetchCountryStates(
          String(this.country_code).toUpperCase()
        ).finally(() => {
          this.state_code = this.findState(this.editedAddress.state);
        });
      } else {
        this.country_code = storedCustomerData.country;
      }
      this.editedCustomer.first_name = storedCustomerData.first_name;
      this.editedCustomer.last_name = storedCustomerData.last_name;
      this.editedCustomer.phone_number = storedCustomerData.phone_number;
    },
    searchCustomerIfChanged() {
      if (this.editedCustomer.email !== this.previousEmail) {
        this.searchCustomer();
        this.previousEmail = this.editedCustomer.email;
      }
    },
    clearCurrentCustomerData() {
      this.editedCustomer = {
        first_name: "",
        last_name: "",
        email: "",
        phone_number: "",
      };
      this.editedAddress = {
        address1: "",
        address2: "",
        city: "",
        country: "",
        state: "",
        zip_code: "",
      };
    },
    searchCustomer() {
      this.customerUIState.validating = true;
      this.globalSearch({
        criteria: this.editedCustomer.email,
        model: "Customer",
        keep_previous_data: false,
      })
        .then((response) => {
          if (!response.found) {
            return;
          }
          this.customerUIState.valid = true;
          this.customerUIState.authorized = !this.isObfuscatedData(
            response.email
          );
          if (this.customerUIState.authorized) {
            this.fetchCustomerData();
          } else {
            this.clearCurrentCustomerData();
            this.editedCustomer.email = this.previousEmail;
          }
        })
        .catch((error) => {
          this.customerUIState.valid = false;
          this.customerUIState.authorized = false;
          this.clearCurrentCustomerData();
          this.editedCustomer.email = this.previousEmail;
          console.log(error);
        })
        .finally(() => {
          this.customerUIState.validating = false;
        });
    },
  },
};
</script>

<style>
.order-return-edit-customer-details {
  gap: 8px;

  .title {
    height: 36px;
    font-style: normal;
    font-weight: 500;
    font-size: 24px;
    line-height: 36px;
    display: flex;
    align-items: flex-end;
    color: #424242;
    flex: none;
    align-self: stretch;
    flex-grow: 0;
  }

  .description {
    height: 24px;
    font-style: normal;
    font-weight: 300;
    font-size: 16px;
    line-height: 24px;
    display: flex;
    align-items: flex-end;
    color: #424242;
    flex: none;
    align-self: stretch;
    flex-grow: 0;
  }

  .section-header {
    font-style: normal;
    font-weight: 500;
    font-size: 20px;
    line-height: 30px;
    color: #424242;
  }

  .edit-customer-details {
    max-width: 544px;
    padding: 0px;
  }

  .gap-16 {
    gap: 16px;
  }

  .input-label {
    height: 18px;
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 18px;
    display: flex;
    align-items: flex-end;
    color: #424242;
    margin-top: 6px;
    margin-bottom: 4px;
  }

  .input-field,
  .select-field input {
    box-sizing: border-box;
    background: #ffffff;
    border: 1px solid #81919f;
    border-radius: 4px;

    padding: 12px 16px;
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 24px;
    color: #6e6e6e;

    height: unset;
  }

  .input-field:focus,
  .input-field:focus-within {
    border: 2px solid #1998d5 !important;
    box-shadow: none !important;
  }

  .input-error {
    height: 18px;
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 18px;
    color: #d4231a;
  }

  .el-input__inner {
    height: 46px !important;
    padding: 12px 16px;
  }
  .el-switch__label * {
    line-height: 1;
    font-size: 15px;
    font-weight: normal;
    display: inline-block;
  }
}
</style>
